import axios from 'axios';
import * as cheerio from 'cheerio';
import { analyzeContent, convertToDialogue } from './openaiService';
import { generateVoiceAudio } from './elevenLabsService';
import { Language } from '../translations';

const CORS_PROXIES = [
  'https://corsproxy.io/?',
  'https://api.allorigins.win/raw?url=',
  'https://api.codetabs.com/v1/proxy?quest='
];

async function tryProxies(url: string): Promise<string> {
  let lastError;

  for (const proxy of CORS_PROXIES) {
    try {
      const response = await axios.get(`${proxy}${encodeURIComponent(url)}`, {
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
          'Accept-Language': 'nl-NL,nl;q=0.9,en-US;q=0.8,en;q=0.7',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
        },
        timeout: 15000,
        maxRedirects: 5,
        validateStatus: (status) => status >= 200 && status < 300
      });

      if (response.data) {
        return response.data;
      }
    } catch (error) {
      lastError = error;
      continue;
    }
  }

  throw lastError;
}

async function extractContent(url: string): Promise<string> {
  try {
    const html = await tryProxies(url);
    const $ = cheerio.load(html);

    // Remove unwanted elements
    $('script, style, nav, footer, header, aside, iframe, .advertisement, .comments, .social-share, .cookie-notice, .popup, .modal').remove();

    // Get main content
    let content = '';

    // Try to find the main content area
    const mainSelectors = [
      'article',
      '[role="main"]',
      '.post-content',
      '.article-content',
      '.entry-content',
      'main',
      '#main-content',
      '.content',
      '#content',
      '.post',
      '.article'
    ];

    for (const selector of mainSelectors) {
      const element = $(selector);
      if (element.length) {
        content = element.text();
        break;
      }
    }

    // If no main content found, get all paragraphs
    if (!content) {
      content = $('p').map((_, el) => $(el).text()).get().join('\n\n');
    }

    // Clean the content
    content = content
      .replace(/\s+/g, ' ')
      .replace(/\n\s*\n/g, '\n')
      .replace(/[^\S\r\n]+/g, ' ')
      .trim();

    if (!content || content.length < 100) {
      throw new Error('Niet genoeg inhoud gevonden op de pagina. Probeer een andere URL.');
    }

    // Limit content length for API
    return content.slice(0, 3000);
  } catch (error: any) {
    console.error('Content extraction error:', error);
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('De verbinding is verlopen. Controleer je internetverbinding en probeer het opnieuw.');
      }
      if (error.response?.status === 404) {
        throw new Error('De opgevraagde pagina kon niet worden gevonden. Controleer de URL.');
      }
      if (error.response?.status === 403) {
        throw new Error('Toegang tot deze pagina is niet toegestaan. Probeer een andere URL.');
      }
      if (!navigator.onLine) {
        throw new Error('Geen internetverbinding. Controleer je verbinding en probeer het opnieuw.');
      }
      throw new Error('Kan de inhoud niet ophalen. Controleer de URL en je internetverbinding.');
    }
    throw new Error('Kan de inhoud niet verwerken: ' + error.message);
  }
}

export async function processUrl(url: string, language: Language): Promise<{ audioUrl: string; dialogue: string }> {
  try {
    // Validate URL
    try {
      new URL(url);
    } catch {
      throw new Error('Ongeldige URL. Voer een geldige webpagina URL in.');
    }

    // Extract content
    console.log('Extracting content...');
    const content = await extractContent(url);
    if (!content) {
      throw new Error('Geen inhoud gevonden op de URL.');
    }

    // Analyze content
    console.log('Analyzing content...');
    const analysis = await analyzeContent(content, language);
    if (!analysis) {
      throw new Error('Analyse van de inhoud is mislukt.');
    }

    // Convert to dialogue
    console.log('Generating dialogue...');
    const dialogue = await convertToDialogue(analysis, language);
    if (!dialogue) {
      throw new Error('Genereren van dialoog is mislukt.');
    }

    // Generate audio
    console.log('Generating audio...');
    const audioUrl = await generateVoiceAudio(dialogue, language);
    if (!audioUrl) {
      throw new Error('Genereren van audio is mislukt.');
    }

    return { audioUrl, dialogue };
  } catch (error: any) {
    console.error('URL Processing Error:', error);
    throw new Error(error.message || 'Er is een onverwachte fout opgetreden.');
  }
}